import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { apiClient } from '../lib/api';
import { Role } from '../types';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import RoleModal from './RoleModal';
import { translatePermission, translateRole } from '../utils/translations';
import { AxiosError } from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';

const RolesList: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | undefined>(undefined);

  const { admin } = useAuth();
  const canViewPermissions = hasPermission(admin, 'permissions', 'read');
  if (!hasPermission(admin, 'roles', 'read')) {
    return <div className="text-red-600">У вас нет прав для просмотра ролей.</div>;
  }

  const { data, isLoading, error, refetch } = useQuery(
    ['roles', page, search],
    () => apiClient.getRoles({
      page,
      limit: 10,
      search,
    })
  );

  const { data: permissionsData, error: permissionsError, isLoading: isPermissionsLoading } = useQuery(
    ['permissions', 'for-roles-list'],
    () => apiClient.getPermissions({ limit: 100 }),
    { staleTime: 60000, enabled: canViewPermissions }
  );

  const handleDelete = async (id: string) => {
    if (window.confirm('Вы уверены, что хотите удалить эту роль?')) {
      try {
        await apiClient.deleteRole(id);
        toast.success('Роль удалена успешно');
        queryClient.invalidateQueries(['roles']);
      } catch (error: any) {
        toast.error(error.response?.data?.error || 'Ошибка удаления роли');
      }
    }
  };

  const handleCreateRole = () => {
    setEditingRole(undefined);
    setIsModalOpen(true);
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setIsModalOpen(true);
  };

  const handleSaveRole = async (roleData: any) => {
    try {
      if (editingRole) {
        await apiClient.updateRole(editingRole.id, roleData);
        // Обновляем данные локально вместо полной перезагрузки
        queryClient.setQueryData(['roles', page, search], (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((role: Role) => 
              role.id === editingRole.id ? { ...role, ...roleData } : role
            )
          };
        });
        toast.success('Роль обновлена успешно');
      } else {
        await apiClient.createRole(roleData);
        toast.success('Роль создана успешно');
        refetch(); // Для новых элементов нужна перезагрузка
      }
      setIsModalOpen(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Ошибка сохранения роли');
    }
  };

  if (isLoading) return <div>Загрузка...</div>;
  if (error) {
    const status = (error as AxiosError)?.response?.status;
    if (status === 403) {
      return <div className="text-red-600">У вас нет прав для просмотра ролей. Обратитесь к администратору.</div>;
    }
    return <div>Ошибка загрузки ролей</div>;
  }

  const roles = data?.data || [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Роли</h1>
        <button 
          onClick={handleCreateRole}
          className="btn btn-primary flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Создать роль
        </button>
      </div>

      {/* Search */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 min-w-0">
          <input
            type="text"
            placeholder="Поиск ролей..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map((role) => (
          <div key={role.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{translateRole(role)}</h3>
                <p className="text-sm text-gray-600 mt-1">{role.description}</p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditRole(role)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(role.id)}
                  className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            {/* Права: показывать только если permissions не загружаются и нет ошибки 403 */}
            {!isPermissionsLoading && canViewPermissions && !(permissionsError && (permissionsError as AxiosError)?.response?.status === 403) && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Права:</h4>
                <div className="flex flex-wrap gap-1">
                  {role.permissions.length === 0 ? (
                    <span className="text-sm text-gray-500">Нет прав</span>
                  ) : (
                    role.permissions.map((permission) => (
                      <span
                        key={permission.id}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {translatePermission(permission)}
                      </span>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex justify-center">
          <nav className="flex space-x-2">
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => setPage(pageNum)}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  pageNum === page
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                {pageNum}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Role Modal */}
      <RoleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        role={editingRole}
        onSave={handleSaveRole}
      />
    </div>
  );
};

export default RolesList; 